import { BaseServiceFacade, GenericApiCall } from 'src/app/common/store/types';
import { AppState } from '../types';
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';
import {
  GroupedLabelWithValue,
  InputReporting,
  LabelWithValue,
  MarketingPerformanceQuery,
  MarketingPerformanceReportItem,
  Period,
  SalesPerformanceFacetItem,
  SalesPerformanceFacetQuery,
  SalesPerformanceQuery,
  SalesPerformanceReportItem,
  CallsPerformanceReportItem,
  CallsPerformanceQuery,
} from './types';
import { reportingActions } from './reporting.actions';
import { reportingSelectors } from './reporting.selectors';

@Injectable()
export class ReportingService extends BaseServiceFacade {
  constructor(store: Store<AppState>) {
    super(store);
  }

  getReportByFilter: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportByFilter,
      reportingSelectors.getReportByFilter,
      true,
    );

  getReportPeopleByRegime: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportPeopleByRegime,
      reportingSelectors.getReportPeopleByRegime,
      true,
    );

  getReportFemalePeopleByAge: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportFemalePeopleByAge,
      reportingSelectors.getReportFemalePeopleByAge,
      true,
    );

  getReportMalePeopleByAge: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportMalePeopleByAge,
      reportingSelectors.getReportMalePeopleByAge,
      true,
    );

  getLeadsByDayAndHoursRange: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getLeadsByDayAndHoursRange,
      reportingSelectors.getLeadsByDayAndHoursRange,
      true,
    );

  getReportLeadsByCity: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportLeadsByCity,
      reportingSelectors.getReportLeadsByCity,
      true,
    );

  getPeriods: GenericApiCall<Period[], Date> = this.genericApiCall(
    reportingActions.getPeriods,
    reportingSelectors.getPeriods,
    true,
  );

  getLeadCount: GenericApiCall<number, InputReporting> = this.genericApiCall(
    reportingActions.getLeadCount,
    reportingSelectors.getLeadCount,
    true,
  );
  getConversionRateCount: GenericApiCall<number, InputReporting> =
    this.genericApiCall(
      reportingActions.getConversionRateCount,
      reportingSelectors.getConversionRateCount,
      true,
    );
  getRawContractCount: GenericApiCall<number, InputReporting> =
    this.genericApiCall(
      reportingActions.getRawContractCount,
      reportingSelectors.getRawContractCount,
      true,
    );
  getValidatedContractCount: GenericApiCall<number, InputReporting> =
    this.genericApiCall(
      reportingActions.getValidatedContractCount,
      reportingSelectors.getValidatedContractCount,
      true,
    );
  getReportLeadsByCategory: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportLeadsByCategory,
      reportingSelectors.getReportLeadsByCategory,
      true,
    );
  getReportContractsByProductCategory: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportContractsByProductCategory,
      reportingSelectors.getReportContractsByProductCategory,
      true,
    );
  getReportLeadsBySource: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportLeadsBySource,
      reportingSelectors.getReportLeadsBySource,
      true,
    );
  getReportContractsBySource: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportContractsBySource,
      reportingSelectors.getReportContractsBySource,
      true,
    );
  getReportLeadsBySalesPerson: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportLeadsBySalesPerson,
      reportingSelectors.getReportLeadsBySalesPerson,
      true,
    );
  getReportContractsBySalesPerson: GenericApiCall<any[], InputReporting> =
    this.genericApiCall(
      reportingActions.getReportContractsBySalesPerson,
      reportingSelectors.getReportContractsBySalesPerson,
      true,
    );
  getReportMonthlyProgress: GenericApiCall<
    GroupedLabelWithValue[],
    InputReporting
  > = this.genericApiCall(
    reportingActions.getReportMonthlyProgress,
    reportingSelectors.getReportMonthlyProgress,
    true,
  );
  getReportSalesTotalAndObjectives: GenericApiCall<
    GroupedLabelWithValue[],
    InputReporting
  > = this.genericApiCall(
    reportingActions.getReportSalesTotalAndObjectives,
    reportingSelectors.getReportSalesTotalAndObjectives,
    true,
  );
  getSalesPerformance: GenericApiCall<
    SalesPerformanceReportItem[],
    SalesPerformanceQuery
  > = this.genericApiCall(
    reportingActions.getSalesPerformance,
    reportingSelectors.getSalesPerformance,
    true,
  );
  getSalesPerformanceFacetItems: GenericApiCall<
    SalesPerformanceFacetItem[],
    SalesPerformanceFacetQuery
  > = this.genericApiCall(
    reportingActions.getSalesPerformanceFacetItems,
    reportingSelectors.getSalesPerformanceFacetItems,
    true,
  );
  getCallsPerformance: GenericApiCall<
    CallsPerformanceReportItem[],
    CallsPerformanceQuery
  > = this.genericApiCall(
    reportingActions.getCallsPerformance,
    reportingSelectors.getCallsPerformance,
    true,
  );
  getMarketingPerformance: GenericApiCall<
    MarketingPerformanceReportItem[],
    MarketingPerformanceQuery
  > = this.genericApiCall(
    reportingActions.getMarketingPerformance,
    reportingSelectors.getMarketingPerformance,
    true,
  );

  setBarGraphStartDate = (barGraphStartDate: string) => {
    this.store.dispatch(
      reportingActions.setBarGraphStartDate({
        barGraphStartDate: barGraphStartDate,
      }),
    );
  };

  setBarGraphEndDate = (barGraphEndDate: string) => {
    this.store.dispatch(
      reportingActions.setBarGraphEndDate({
        barGraphEndDate: barGraphEndDate,
      }),
    );
  };

  getBarGraphStartDate = () =>
    this.store.select(reportingSelectors.getBarGraphStartDate);

  getBarGraphEndDate = () =>
    this.store.select(reportingSelectors.getBarGraphEndDate);
}
